import {
  AfterViewInit,
  DestroyRef,
  Directive,
  ElementRef,
  HostListener,
  inject,
  isDevMode,
  OnInit,
  signal,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { APP_MASONRY, fromResize, ItemPosition, MasonryContainer, MasonryItem } from './masonry.token';

@Directive({
  selector: '[appMasonryItem]',
  host: { style: 'position: absolute; visibility: hidden' },
})
export class AppMasonryItemDirective implements OnInit, AfterViewInit, MasonryItem {
  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly masonry = inject<MasonryContainer>(APP_MASONRY);
  private readonly destroyRef = inject(DestroyRef);

  private imageQueue!: Set<HTMLImageElement>;
  private readonly readySubject = new Subject<void>();
  private isReady = false;
  private readonly settled = signal(false);
  private dismissing = false;

  element(): Element {
    return this.el.nativeElement;
  }

  comparePosition(other: MasonryItem): number {
    return this.el.nativeElement.compareDocumentPosition(other.element()) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  }

  dimensions(): { width: number; height: number } {
    const el = this.el.nativeElement;
    const s = getComputedStyle(el);
    return {
      width: el.offsetWidth + parseFloat(s.marginLeft) + parseFloat(s.marginRight),
      height: el.offsetHeight + parseFloat(s.marginTop) + parseFloat(s.marginBottom),
    };
  }

  applyPosition(pos: ItemPosition): void {
    if (this.dismissing) return;
    this.el.nativeElement.style.visibility = '';
    this.el.nativeElement.style[pos.originLeft ? 'left' : 'right'] = pos.x;
    this.el.nativeElement.style.top = pos.y;
    if (!this.settled()) {
      const { enterClass, settledClass } = this.masonry.options().animations ?? {};
      this.settle(enterClass, settledClass);
    }
  }

  ready$(): Observable<void> {
    return this.readySubject.asObservable();
  }

  resize$(): Observable<void> {
    return fromResize(this.el.nativeElement);
  }

  dismiss(): void {
    this.dismissing = true;
    const el = this.el.nativeElement;
    const { enterClass, leaveClass } = this.masonry.options().animations ?? {};
    if (leaveClass) {
      if (enterClass) el.classList.remove(enterClass);
      el.classList.add(leaveClass);
      el.addEventListener('animationend', e => {
        if (e.target !== el) return;
        el.remove();
      });
    } else {
      el.remove();
    }
  }

  ngOnInit(): void {
    this.masonry.register(this);
  }

  ngAfterViewInit(): void {
    const images = Array.from(this.el.nativeElement.getElementsByTagName('img'));
    this.imageQueue = new Set(images);

    if (images.length === 0) {
      setTimeout(() => this.markReady());
    } else {
      for (const img of images) {
        if (img.hasAttribute('masonryLazy')) {
          this.imageLoaded(img);
        } else {
          const onLoad = () => this.imageLoaded(img);
          img.addEventListener('load', onLoad);
          img.addEventListener('error', onLoad);
        }
      }
    }

    this.destroyRef.onDestroy(() => {
      if (!this.dismissing && this.isReady && this.el.nativeElement.parentNode) {
        const { leaveClass } = this.masonry.options().animations ?? {};
        if (leaveClass) {
          const el = this.el.nativeElement;
          el.parentNode!.appendChild(el);
          el.classList.add(leaveClass);
          el.addEventListener('animationend', () => el.remove(), { once: true });
        }
      }
    });
  }

  settle(enterClass?: string, settledClass?: string): void {
    if (enterClass) this.el.nativeElement.classList.add(enterClass);
    if (settledClass) {
      this.el.nativeElement.addEventListener(
        'animationend',
        () => {
          this.el.nativeElement.classList.add(settledClass);
          this.settled.set(true);
        },
        { once: true }
      );
    } else {
      this.settled.set(true);
    }
  }

  private markReady(): void {
    if (this.isReady) return;
    this.isReady = true;
    this.readySubject.next();
    this.readySubject.complete();
  }

  private imageLoaded(img: HTMLImageElement): void {
    this.imageQueue.delete(img);
    if (this.imageQueue.size === 0) {
      this.markReady();
    }
  }

  @HostListener('click', ['$event'])
  onClick(e: MouseEvent): void {
    if (isDevMode() && e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      this.dismiss();
    }
  }
}
